import React from "react";
import { Link, Outlet, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BeakerIcon,
  ChartBarIcon,
  ChevronRightIcon,
  ReceiptPercentIcon,
  WalletIcon,
} from "@heroicons/react/24/outline";

import Loading from "@components/shared/Loading";
import { useAppContext } from "@context/AppContext";
import { VIEW_TRANSACTIONS_PERMISSIONS } from "@config/zenithPermissions";
import LoadFailed, { showsOwnFailure } from "@components/zenith/ui/LoadFailed";
import PageHeading from "@components/zenith/ui/PageHeading";
import StatCard from "@components/zenith/ui/StatCard";
import Tabs from "@components/zenith/ui/Tabs";
import { useSubscriberLevel0Resource } from "@hooks/query/zenith/useSubscriberLevel0Resource";
import { formatVolume } from "@utils/formatVolume";
import { zoneVolumes } from "@utils/volumes";

export default function ZoneAccount() {
  const { t } = useTranslation();
  const { id: level0ResourceId = "" } = useParams();
  const { checkPermissions } = useAppContext();
  // The tabs fetch their own data, so they load whatever becomes of the zone's.
  const {
    data: zone,
    isLoading,
    isError,
  } = useSubscriberLevel0Resource({
    params: { level0ResourceId },
    ...showsOwnFailure,
  });

  const title = zone
    ? `${t("zenith.zone_accounts.title")} ${zone.identifier}`
    : t("zenith.zone_accounts.title");

  return (
    <div className="space-y-5">
      <nav aria-label={t("common.breadcrumb") as string}>
        <ol className="flex items-center gap-1.5 text-sm">
          <li>
            <Link to="/zenith/dashboard" className="text-primary-1 hover:underline">
              {t("zenith.navigation.dashboard")}
            </Link>
          </li>
          <li aria-hidden="true">
            <ChevronRightIcon className="h-3.5 w-3.5 text-gray-400" />
          </li>
          <li aria-current="page" className="text-gray-500">
            {title}
          </li>
        </ol>
      </nav>

      <PageHeading title={title} sub={zone?.name} />

      {isLoading ? (
        <Loading />
      ) : isError ? (
        <LoadFailed />
      ) : (
        <ZoneTiles zone={zone} />
      )}

      <div className="rounded-lg border border-gray-200 bg-white">
        <Tabs
          label={title}
          tabs={[
            { label: t("zenith.zone_accounts.tabs.summary"), to: "summary" },
            {
              label: t("zenith.zone_accounts.tabs.water_entitlements"),
              to: "entitlements",
            },
            {
              label: t("zenith.zone_accounts.tabs.offtakes_and_meters"),
              to: "offtakes",
            },
            ...(checkPermissions(VIEW_TRANSACTIONS_PERMISSIONS)
              ? [
                  {
                    label: t("zenith.zone_accounts.tabs.transaction_history"),
                    to: "transactions",
                  },
                ]
              : []),
          ]}
        />
        <div className="p-5">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

/** The zone's period figures; between periods, each says there is no active period. */
const ZoneTiles: React.FunctionComponent<{ zone: any }> = ({ zone }) => {
  const { t } = useTranslation();
  const noPeriod = zone.balance === null;
  const { remaining, allocated, used } = zoneVolumes(zone);
  const usageRate = allocated > 0 ? (used / allocated) * 100 : undefined;
  const noPeriodText = t("zenith.dashboard.no_active_period");
  const figure = (litres: number) =>
    noPeriod ? noPeriodText : formatVolume(litres);

  return (
    <section
      aria-label={t("common.summary") as string}
      // Columns follow the content width, which the info panel narrows.
      className="grid grid-cols-fit-40 gap-4"
    >
      <StatCard
        label={t("zenith.zone_accounts.remaining_balance")}
        value={figure(remaining)}
        icon={BeakerIcon}
        tone={remaining < 0 ? "danger" : "success"}
      />
      <StatCard
        label={t("zenith.zone_accounts.allocated")}
        value={figure(allocated)}
        icon={WalletIcon}
        tone="info"
      />
      <StatCard
        label={t("zenith.zone_accounts.used")}
        value={figure(used)}
        icon={ChartBarIcon}
        tone="warning"
      />
      <StatCard
        label={t("zenith.zone_accounts.usage_rate")}
        value={
          noPeriod
            ? noPeriodText
            : usageRate === undefined
              ? "-"
              : `${usageRate.toFixed(1)}%`
        }
        icon={ReceiptPercentIcon}
        tone={usageRate !== undefined && usageRate > 90 ? "danger" : "primary"}
      />
    </section>
  );
};
